import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { getTaskStatusApi } from '../../service/task';
import Hls from 'hls.js';
import './SubtitleDemo.scss';
import { VIDEO_CDN_PREFIX } from '../../config/video';

export interface Subtitle {
  start: number;
  end: number;
  text: string;
}

// 直接控制接口
export interface SubtitleDemoRef {
  seekToTime: (time: number) => void;
  play: () => void;
  pause: () => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  isPlaying: () => boolean;
}

export interface SubtitleDemoProps {
  taskId?: string; // 任务ID，如果不提供则使用默认值
  srtContent?: string; // 自定义SRT内容，如果不提供则使用默认内容
  showControls?: boolean; // 是否显示播放控制按钮
  className?: string; // 自定义CSS类名
  videoData?: Task.GetTaskStatusResult | null; // 视频数据，包含pure_video_hls_url
  localSubtitles?: Task.SubtitlesLegacy[]; // 本地字幕数据，优先使用此数据
  targetText?: Array<{ id: number; text: string; timeRange: string }>; // 右侧台词编辑区数据，用于获取最新时间戳
  // 外部播放控制
  externalIsPlaying?: boolean; // 外部播放状态
  externalCurrentTime?: number; // 外部当前时间，用于同步
  onExternalPlay?: () => void; // 外部播放控制回调
  onExternalPause?: () => void; // 外部暂停控制回调
  onExternalSeek?: (time: number) => void; // 外部跳转控制回调
  onSubtitleChange?: (subtitles: Subtitle[]) => void; // 字幕变化回调
  onRightVideoReady?: () => void; // 右侧视频就绪回调
  overlayTopPx?: number; // 画布顶部偏移（px），用于根据角色调整
}

const SubtitleDemo = forwardRef<SubtitleDemoRef, SubtitleDemoProps>(({
  taskId,
  srtContent,
  showControls = true,
  className = '',
  videoData,
  localSubtitles,
  targetText,
  externalIsPlaying,
  externalCurrentTime,
  onExternalPlay,
  onExternalPause,
  onExternalSeek,
  onSubtitleChange,
  onRightVideoReady
  , overlayTopPx = 10
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trackRef = useRef<HTMLTrackElement>(null);
  
  // 状态管理
  const [fetchedSrtContent, setFetchedSrtContent] = useState<string>('');
  const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
  const resizeObserverRef = useRef<ResizeObserver | null>(null); // ResizeObserver 引用
  const rafIdRef = useRef<number | null>(null); // requestAnimationFrame ID
  const renderRafIdRef = useRef<number | null>(null); // 字幕渲染的 requestAnimationFrame ID
  const lastRenderTimeRef = useRef<number>(0); // 上次渲染时间
  
  // Canvas相关
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);

  // 暴露直接控制方法给父组件
  useImperativeHandle(ref, () => ({
    seekToTime: (time: number) => {
      // 直接控制跳转到时间
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
    },
    play: () => {
      // 直接控制播放
      if (videoRef.current) {
        videoRef.current.play().catch(error => {
          console.error('SubtitleDemo: 直接播放失败:', error);
        });
      }
    },
    pause: () => {
      // 直接控制暂停
      if (videoRef.current) {
        videoRef.current.pause();
      }
    },
    getCurrentTime: () => {
      return videoRef.current?.currentTime || 0;
    },
    getDuration: () => {
      return videoRef.current?.duration || 0;
    },
    isPlaying: () => {
      return videoRef.current ? !videoRef.current.paused : false;
    }
  }), []);

  // 默认SRT字幕内容
  const defaultSrtContent = `1
00:00:01,000 --> 00:00:04,000
欢迎观看SRT字幕演示

2
00:00:04,500 --> 00:00:08,000
这是使用SRT技术实现的可编辑字幕

3
00:00:08,500 --> 00:00:12,000
支持多种样式和特效

4
00:00:12,500 --> 00:00:16,000
这是说话人1的台词
使用不同的颜色和背景

5
00:00:16,500 --> 00:00:20,000
这是说话人2的台词
每个说话人都有独特的样式

6
00:00:20,500 --> 00:00:24,000
这是高亮显示的重要信息
背景色和文字颜色都不同

7
00:00:24,500 --> 00:00:28,000
字幕支持实时编辑和更新
无需重新加载视频

8
00:00:28,500 --> 00:00:32,000
感谢观看SRT字幕演示！`;

  // 使用获取到的字幕内容、传入的SRT内容或默认内容 - 使用useMemo优化
  const currentSrtContent = useMemo(() => {
    return fetchedSrtContent || srtContent || defaultSrtContent;
  }, [fetchedSrtContent, srtContent]);

  // 解析时间格式 - 与videoEdit中的parseTimeToSeconds保持一致
  const parseTime = useCallback((timeString: string): number => {
    // 支持格式: "00:00:22,566" 或 "00:00:22.566"
    const timeStr = timeString.replace(',', '.');
    const parts = timeStr.split(':');
    
    if (parts.length === 3) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseFloat(parts[2]);
      return hours * 3600 + minutes * 60 + seconds;
    } else if (parts.length === 2) {
      // 支持 "00:22.566" 格式
      const minutes = parseInt(parts[0], 10);
      const seconds = parseFloat(parts[1]);
      return minutes * 60 + seconds;
    } else {
      // 纯秒数格式
      return parseFloat(timeStr) || 0;
    }
  }, []);

  // 解析字幕数据 - 优先使用videoData.subtitles，回退到SRT内容
  const parseSubtitles = useCallback(() => {
    let parsedSubtitles: Subtitle[] = [];
    
    // 优先使用videoData.subtitles数据
    if (videoData?.subtitles && Array.isArray(videoData.subtitles)) {
      // console.log('SubtitleDemo: 使用videoData.subtitles数据:', videoData.subtitles);
      
      // 创建targetText的Map，用于快速查找最新时间戳和文本
      const targetTextMap = new Map<number, { timeRange?: string; text?: string }>();
      if (targetText && Array.isArray(targetText)) {
        targetText.forEach(item => {
          targetTextMap.set(item.id, { 
            timeRange: item.timeRange,
            text: item.text
          });
        });
      }

      parsedSubtitles = videoData.subtitles.map((subtitle: Task.SubtitlesLegacy, index: number) => {
        // 优先使用targetText中的最新时间戳和文本（确保与右侧台词编辑区保持一致）
        const targetData = targetTextMap.get(subtitle.id);
        let startTime: string;
        let endTime: string;
        let text: string;

        if (targetData && targetData.timeRange) {
          // 使用targetText中的最新时间戳
          const timeRangeParts = targetData.timeRange.split(' → ');
          if (timeRangeParts.length === 2) {
            startTime = timeRangeParts[0].trim();
            endTime = timeRangeParts[1].trim();
          } else {
            // timeRange格式错误，回退到videoData
            startTime = subtitle.post_start_time || subtitle.origin_start_time;
            endTime = subtitle.post_end_time || subtitle.origin_end_time;
          }
        } else {
          // targetText中没有该字幕，使用videoData中的时间戳
          // 优先使用post时间戳，如果没有则回退到origin时间戳
          startTime = subtitle.post_start_time || subtitle.origin_start_time;
          endTime = subtitle.post_end_time || subtitle.origin_end_time;
        }

        // 优先使用targetText中的文本，如果没有则使用videoData中的post_text
        text = targetData?.text || subtitle.post_text || '';

        return {
          start: parseTime(startTime),
          end: parseTime(endTime),
          text: text, // 优先使用targetText中的文本
          id: subtitle.id || index + 1
        };
      });
      
      // console.log('SubtitleDemo: 从videoData.subtitles解析的字幕数据:', parsedSubtitles);
    } else {
      // 回退到SRT内容解析
      // console.log('SubtitleDemo: 回退到SRT内容解析');
      
      const lines = currentSrtContent.split('\n');
      let currentSubtitle: Subtitle | null = null;
      let isTimeLine = false;
      
      lines.forEach(line => {
        const trimmedLine = line.trim();
        
        // 跳过空行
        if (!trimmedLine) {
          return;
        }
        
        // 检查是否是序号行（纯数字）
        if (/^\d+$/.test(trimmedLine)) {
          // 如果遇到新的序号，保存当前字幕
          if (currentSubtitle && currentSubtitle.text) {
            parsedSubtitles.push({ ...currentSubtitle });
          }
          // 开始新的字幕块
          currentSubtitle = null;
          isTimeLine = false;
          return;
        }
        
        // 检查是否是时间行
        if (trimmedLine.includes('-->')) {
          const [start, end] = trimmedLine.split(' --> ');
          currentSubtitle = {
            start: parseTime(start.trim()),
            end: parseTime(end.trim()),
            text: ''
          };
          isTimeLine = true;
        } else if (isTimeLine && currentSubtitle) {
          // 时间行后的内容就是字幕文本
          if (currentSubtitle.text) {
            currentSubtitle.text += '\n' + trimmedLine;
          } else {
            currentSubtitle.text = trimmedLine;
          }
        } else if (currentSubtitle && !isTimeLine) {
          // 继续添加文本内容
          if (currentSubtitle.text) {
            currentSubtitle.text += '\n' + trimmedLine;
          } else {
            currentSubtitle.text = trimmedLine;
          }
        }
      });
      
      // 保存最后一个字幕
      if (currentSubtitle && (currentSubtitle as Subtitle).text) {
        const subtitle: Subtitle = {
          start: (currentSubtitle as Subtitle).start,
          end: (currentSubtitle as Subtitle).end,
          text: (currentSubtitle as Subtitle).text
        };
        parsedSubtitles.push(subtitle);
      }
      
      // 从SRT内容解析的字幕数据
    }
    
    setSubtitles(parsedSubtitles);
    
    // 通知父组件字幕数据变化
    if (onSubtitleChange) {
      onSubtitleChange(parsedSubtitles);
    }
  }, [parseTime, onSubtitleChange, currentSrtContent, videoData, targetText]);

  // 获取字幕内容
  const fetchSubtitleContent = useCallback(async (): Promise<string> => {
    try {
      // 优先使用本地字幕数据
      if (localSubtitles && localSubtitles.length > 0) {
        console.log('使用本地字幕数据，字幕数量:', localSubtitles.length);
        // 将本地字幕数据转换为SRT格式
        const srtContent = localSubtitles.map((subtitle, index) => {
          const startTime = subtitle.post_start_time || subtitle.origin_start_time;
          const endTime = subtitle.post_end_time || subtitle.origin_end_time;
          const text = subtitle.post_text || ''; // 当目标台词为空时，不显示原台词
          
          // 转换时间格式从 "00:00:00,000" 到 "00:00:00.000"
          const formatTime = (time: string) => time.replace(',', '.');
          
          return `${index + 1}\n${formatTime(startTime)} --> ${formatTime(endTime)}\n${text}\n`;
        }).join('\n');
        
        return srtContent;
      }
      
      // 如果没有本地字幕数据，优先使用传入的videoData中的翻译后字幕
      if (videoData && videoData.translated_subtitle_url) {
        const subtitleUrl = `${VIDEO_CDN_PREFIX}${videoData.translated_subtitle_url}`;
        console.log('获取翻译后字幕内容:', subtitleUrl);
        
        const response = await fetch(subtitleUrl);
        if (response.ok) {
          const content = await response.text();
          // console.log('翻译后字幕内容获取成功:', content.substring(0, 100) + '...');
          return content;
        } else {
          throw new Error(`翻译后字幕获取失败: ${response.status}`);
        }
      }
      
      // 如果没有翻译后字幕，尝试使用原始字幕
      if (videoData && videoData.subtitle_url) {
        const subtitleUrl = `${VIDEO_CDN_PREFIX}${videoData.subtitle_url}`;
        // console.log('获取原始字幕内容:', subtitleUrl);
        
        const response = await fetch(subtitleUrl);
        if (response.ok) {
          const content = await response.text();
          // console.log('原始字幕内容获取成功:', content.substring(0, 100) + '...');
          return content;
        } else {
          throw new Error(`原始字幕获取失败: ${response.status}`);
        }
      }
      
      // 如果都没有，使用默认字幕
      // console.log('使用默认字幕内容');
      return defaultSrtContent;
    } catch (error) {
      console.error('获取字幕内容失败:', error);
      // 如果获取失败，使用默认字幕
      return defaultSrtContent;
    }
  }, [videoData, localSubtitles]);

  // 获取视频URL
  const fetchVideoUrl = useCallback(async (): Promise<string> => {
    try {
      // 优先使用传入的videoData
      if (videoData && videoData.pure_video_hls_url) {
        console.log('使用传入的videoData中的视频URL:', videoData.pure_video_hls_url);
        return videoData.pure_video_hls_url;
      }
      
      // 如果没有传入videoData，且没有taskId，则使用默认视频
      if (!taskId) {
        console.log('没有传入taskId，使用默认视频');
        return 'https://www.w3schools.com/html/mov_bbb.mp4';
      }
      
      // 如果有taskId，则通过API获取
      const response = await getTaskStatusApi(parseInt(taskId));
      
      if (response && response.pure_video_hls_url) {
        return response.pure_video_hls_url;
      } else {
        throw new Error('未找到视频URL');
      }
    } catch (error) {
      console.error('获取视频URL失败:', error);
      // 如果API调用失败，使用默认视频
      return 'https://www.w3schools.com/html/mov_bbb.mp4';
    }
  }, [taskId, videoData]);

  // 初始化视频
  const initVideo = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    
    try {
      const url = await fetchVideoUrl();
      
      // 设置视频源
      if (url.endsWith('.m3u8')) {
        // HLS视频流
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari原生支持HLS
          video.src = url;
          console.log('使用Safari原生HLS支持');
        } else if (Hls.isSupported()) {
          // 使用HLS.js库
          const hls = new Hls();
          hls.loadSource(url);
          hls.attachMedia(video);
          console.log('使用HLS.js库播放HLS视频流');
        } else {
          console.error('浏览器不支持HLS视频播放');
          video.src = url; // 尝试直接设置，可能会失败
        }
      } else {
        // 普通视频文件
        video.src = url;
        console.log('播放普通视频文件');
      }
      
      // 预加载视频元数据，确保readyState >= 1
      video.preload = 'metadata';
      video.load(); // 强制加载元数据
      
      console.log('视频源已设置:', url);
      
    } catch (error) {
      console.error('初始化视频失败:', error);
    }
  }, [fetchVideoUrl]);

  // 初始化字幕
  const initSubtitles = useCallback(() => {
    const video = videoRef.current;
    const track = trackRef.current;
    
    if (!video || !track) return;
    
    // 创建Blob URL
    const blob = new Blob([currentSrtContent], { type: 'text/srt' });
    const url = URL.createObjectURL(blob);
    
    // 设置字幕轨道源
    track.src = url;
    // 默认隐藏原生字幕，使用Canvas模式
    (track as any).mode = 'hidden';
    
    // 解析字幕数据
    parseSubtitles();
  }, [parseSubtitles]);

  // 文本换行函数（按单词换行）- 先定义，供其他函数使用
  const wrapText = useCallback((text: string, maxWidth: number, ctx: CanvasRenderingContext2D): string[] => {
    // 按空格分割单词，保留空格
    const words = text.split(/(\s+)/);
    const lines: string[] = [];
    let currentLine = '';
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine + word;
      const metrics = ctx.measureText(testLine);
      
      // 如果添加这个单词后超出最大宽度，且当前行不为空
      if (metrics.width > maxWidth && currentLine.trim() !== '') {
        // 保存当前行（去掉末尾空格）
        lines.push(currentLine.trim());
        // 开始新行，如果当前单词不是空格，则作为新行的开始
        currentLine = word.trim() ? word : '';
      } else {
        // 可以添加这个单词
        currentLine = testLine;
      }
    }
    
    // 添加最后一行（如果有内容）
    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }
    
    return lines;
  }, []);

  // 渲染单个字幕的函数（供 renderSubtitles 使用）
  const renderSubtitleInternal = useCallback((subtitle: Subtitle, index: number) => {
    if (!ctx || !canvas) return;
    
    // 基于视频宽度计算比例因子，使字幕随视频同比例缩放
    // 使用一个参考宽度（如1920px）作为基准，计算当前视频相对于基准的比例
    const referenceWidth = 1920; // 参考宽度（可根据实际需求调整）
    const scaleFactor = canvas.width / referenceWidth;
    
    // 基础尺寸（基于参考宽度）
    const baseFontSize = 130; // 参考宽度下的基础字体大小（增大以提升可读性）
    const baseMaxWidthRatio = 0.85; // 字幕最大宽度占视频宽度的比例（增大宽度）
    const baseLineHeightRatio = 1.4; // 行高相对于字体大小的倍数
    const basePaddingRatio = 0.8; // 内边距相对于字体大小的倍数
    const baseStrokeWidthRatio = 0.15; // 描边宽度相对于字体大小的倍数
    
    // 按比例缩放后的尺寸
    const fontSize = baseFontSize * scaleFactor;
    const maxWidth = canvas.width * baseMaxWidthRatio; // 最大宽度始终是视频宽度的85%
    const lineHeight = fontSize * baseLineHeightRatio;
    const padding = fontSize * basePaddingRatio;
    const strokeWidth = fontSize * baseStrokeWidthRatio;
    
    // 设置字体
    ctx.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
    
    // 根据最大宽度换行文本（换行位置基于比例缩放后的宽度，保持相对位置不变）
    const lines = wrapText(subtitle.text, maxWidth, ctx);
    
    // 计算字幕框尺寸
    const textWidth = maxWidth;
    const textHeight = lines.length * lineHeight;
    const bgWidth = textWidth + padding * 2;
    const bgHeight = textHeight + padding * 2;
    
    // 计算字幕框位置（居中，位于画布下方85%处）
    const baseX = canvas.width / 2; // 基础位置：画布中心
    const baseY = canvas.height * 0.85; // 基础位置：画布下方85%处（往下移）
    const x = baseX - bgWidth / 2;
    const y = baseY - bgHeight / 2;
    
    // 设置字体对齐方式
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // 设置文字颜色
    const textColor = '#fff';
    
    // 绘制文字（多行文本，水平和垂直居中）
    // 先绘制黑色描边，再绘制白色填充，实现描边效果
    const startY = y + bgHeight / 2 - (lines.length - 1) * lineHeight / 2;
    
    lines.forEach((line, lineIndex) => {
      const lineY = startY + lineIndex * lineHeight;
      const textX = x + bgWidth / 2;
      
      // 绘制黑色描边
      ctx.strokeStyle = '#000000'; // 黑色描边
      ctx.lineWidth = strokeWidth; // 描边宽度按比例缩放
      ctx.lineJoin = 'round'; // 圆角连接，使描边更平滑
      ctx.miterLimit = 2; // 斜接限制
      ctx.strokeText(line, textX, lineY);
      
      // 绘制白色填充文字
      ctx.fillStyle = textColor;
      ctx.fillText(line, textX, lineY);
    });
  }, [ctx, canvas, wrapText]);

  // 渲染字幕到Canvas（优化版本，避免闪烁）
  const renderSubtitles = useCallback(() => {
    if (!ctx || !canvas || !videoRef.current) return;
    
    // 取消之前的渲染请求，避免重复渲染
    if (renderRafIdRef.current !== null) {
      cancelAnimationFrame(renderRafIdRef.current);
    }
    
    // 使用 requestAnimationFrame 统一调度，确保在下一帧渲染
    renderRafIdRef.current = requestAnimationFrame(() => {
    const video = videoRef.current;
      if (!video) {
        renderRafIdRef.current = null;
        return;
      }
      
    // 优先使用外部传入的时间，确保与左侧视频同步
    const currentTime = externalCurrentTime !== undefined ? externalCurrentTime : video.currentTime;
    
      // 节流：如果距离上次渲染时间太短（小于16ms，约60fps），跳过本次渲染
      const now = performance.now();
      if (now - lastRenderTimeRef.current < 16) {
        renderRafIdRef.current = null;
        return;
      }
      lastRenderTimeRef.current = now;
      
      // 保存当前上下文状态
      ctx.save();
      
      // 清空Canvas（在保存状态后清空，避免影响其他绘制）
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // 渲染当前时间段的字幕（只渲染第一个匹配的字幕，避免同时显示多个）
      for (let index = 0; index < subtitles.length; index++) {
        const subtitle = subtitles[index];
        // 使用 < end 而不是 <= end，避免在边界处同时匹配两个字幕
        // 当时间正好等于前一个字幕的结束时间时，应该匹配后一个字幕
        if (currentTime >= subtitle.start && currentTime < subtitle.end) {
          renderSubtitleInternal(subtitle, index);
          // 找到第一个匹配的字幕后立即退出，避免渲染多个字幕
          break;
        }
      }
      
      // 恢复上下文状态
      ctx.restore();
      
      renderRafIdRef.current = null;
    });
  }, [ctx, canvas, subtitles, externalCurrentTime, renderSubtitleInternal]);

  // 调整Canvas尺寸和位置（优化版本，避免闪烁）
  const resizeCanvas = useCallback(() => {
    const video = videoRef.current;
    const canvasElement = canvasRef.current;
    
    if (!video || !canvasElement) return;
    
    // 取消之前的动画帧请求
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    
    // 使用requestAnimationFrame确保在下一帧更新，实现流畅的实时响应
    rafIdRef.current = requestAnimationFrame(() => {
      const videoRect = video.getBoundingClientRect();
      const containerRect = canvasElement.parentElement?.getBoundingClientRect();
      
      if (!containerRect) {
        rafIdRef.current = null;
        return;
      }
      
      // 检查尺寸是否真的变化了，避免不必要的重绘
      const currentWidth = canvasElement.width;
      const currentHeight = canvasElement.height;
      const newWidth = videoRect.width;
      const newHeight = videoRect.height;
      
      // 只有当尺寸真正变化时才更新（避免频繁清空Canvas导致闪烁）
      if (Math.abs(currentWidth - newWidth) > 1 || Math.abs(currentHeight - newHeight) > 1) {
        // 设置canvas尺寸与video一致（修改width/height会清空画布，但这是必要的）
        canvasElement.width = newWidth;
        canvasElement.height = newHeight;
      }
      
      // 更新样式尺寸（不会清空画布）
      canvasElement.style.width = newWidth + 'px';
      canvasElement.style.height = newHeight + 'px';
      
      // 计算video相对于容器的位置，让canvas与video完全对齐
      const offsetX = videoRect.left - containerRect.left;
      const offsetY = videoRect.top - containerRect.top;
      
      canvasElement.style.left = offsetX + 'px';
      canvasElement.style.top = offsetY + 'px';
      
      // 延迟一帧再渲染字幕，确保Canvas尺寸已更新
      requestAnimationFrame(() => {
      renderSubtitles();
      });
      
      rafIdRef.current = null;
    });
  }, [renderSubtitles]);

  // 初始化Canvas
  const initCanvas = useCallback(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;
    
    const context = canvasElement.getContext('2d');
    if (!context) return;
    
    setCanvas(canvasElement);
    setCtx(context);
    
    // 设置Canvas尺寸
    resizeCanvas();
  }, [resizeCanvas]);




  // 渲染单个字幕
  const renderSubtitle = useCallback((subtitle: Subtitle, index: number) => {
    if (!ctx || !canvas) return;
    
    // 基于视频宽度计算比例因子，使字幕随视频同比例缩放
    // 使用一个参考宽度（如1920px）作为基准，计算当前视频相对于基准的比例
    const referenceWidth = 1920; // 参考宽度（可根据实际需求调整）
    const scaleFactor = canvas.width / referenceWidth;
    
    // 基础尺寸（基于参考宽度）
    const baseFontSize = 130; // 参考宽度下的基础字体大小（增大以提升可读性）
    const baseMaxWidthRatio = 0.85; // 字幕最大宽度占视频宽度的比例（增大宽度）
    const baseLineHeightRatio = 1.4; // 行高相对于字体大小的倍数
    const basePaddingRatio = 0.8; // 内边距相对于字体大小的倍数
    const baseStrokeWidthRatio = 0.15; // 描边宽度相对于字体大小的倍数
    
    // 按比例缩放后的尺寸
    const fontSize = baseFontSize * scaleFactor;
    const maxWidth = canvas.width * baseMaxWidthRatio; // 最大宽度始终是视频宽度的70%
    const lineHeight = fontSize * baseLineHeightRatio;
    const padding = fontSize * basePaddingRatio;
    const strokeWidth = fontSize * baseStrokeWidthRatio;
    
    // 设置字体
    ctx.font = `${fontSize}px Microsoft YaHei, Arial, sans-serif`;
    
    // 根据最大宽度换行文本（换行位置基于比例缩放后的宽度，保持相对位置不变）
    const lines = wrapText(subtitle.text, maxWidth, ctx);
    
    // 计算字幕框尺寸
    const textWidth = maxWidth;
    const textHeight = lines.length * lineHeight;
    const bgWidth = textWidth + padding * 2;
    const bgHeight = textHeight + padding * 2;
    
    // 计算字幕框位置（居中，位于画布下方85%处）
    const baseX = canvas.width / 2; // 基础位置：画布中心
    const baseY = canvas.height * 0.85; // 基础位置：画布下方85%处（往下移）
    const x = baseX - bgWidth / 2;
    const y = baseY - bgHeight / 2;
    
    // 设置字体对齐方式
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // 设置文字颜色
    const textColor = '#fff';
    
    // 绘制文字（多行文本，水平和垂直居中）
    // 先绘制黑色描边，再绘制白色填充，实现描边效果
    const startY = y + bgHeight / 2 - (lines.length - 1) * lineHeight / 2;
    
    lines.forEach((line, lineIndex) => {
      const lineY = startY + lineIndex * lineHeight;
      const textX = x + bgWidth / 2;
      
      // 绘制黑色描边
      ctx.strokeStyle = '#000000'; // 黑色描边
      ctx.lineWidth = strokeWidth; // 描边宽度按比例缩放
      ctx.lineJoin = 'round'; // 圆角连接，使描边更平滑
      ctx.miterLimit = 2; // 斜接限制
      ctx.strokeText(line, textX, lineY);
      
      // 绘制白色填充文字
      ctx.fillStyle = textColor;
      ctx.fillText(line, textX, lineY);
    });
  }, [ctx, canvas, wrapText]);



  // 视频播放控制 - 使用useCallback优化
  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.play();
    }
  }, []);

  const stopVideo = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  // 更新当前字幕显示 - 已合并到 renderSubtitles，此函数保留以兼容旧代码
  const updateCurrentSubtitle = useCallback(() => {
    renderSubtitles();
  }, [renderSubtitles]);

  // 初始化
  useEffect(() => {
    const init = async () => {
      // 先获取字幕内容
      const subtitleContent = await fetchSubtitleContent();
      setFetchedSrtContent(subtitleContent);
      
      // 再初始化视频
      await initVideo();
      // 然后初始化字幕
      initSubtitles();
      // 最后初始化Canvas
      initCanvas();
    };
    
    init();
  }, [fetchSubtitleContent]); // 添加fetchSubtitleContent依赖

  // 监听字幕内容变化，重新解析字幕
  useEffect(() => {
    if (fetchedSrtContent || videoData?.subtitles) {
      parseSubtitles();
    }
  }, [fetchedSrtContent, videoData, targetText, parseSubtitles]);

  // 监听字幕数据变化，立即重新渲染字幕（确保译文变更后视频上的字幕同步更新）
  useEffect(() => {
    if (subtitles.length > 0 && ctx && canvas && videoRef.current) {
      // 立即触发一次字幕渲染，确保显示最新的字幕内容
      // 使用 setTimeout 确保在状态更新后执行
      const timer = setTimeout(() => {
        renderSubtitles();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [subtitles, renderSubtitles, ctx, canvas]);

  // 监听外部播放状态变化 - 使用useLayoutEffect确保在DOM更新前执行
  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video || externalIsPlaying === undefined) return;

    // 检查视频是否已加载元数据，确保readyState >= 1
    if (video.readyState < 1) {
      // 视频元数据未加载完成，等待加载
      return;
    }

    // 只有在非用户交互时才自动同步播放状态
    if (!userInteractionRef.current) {
      if (externalIsPlaying) {
        // 右侧视频静音，只播放画面
        video.muted = true;
        video.play();
        // 同步播放右侧视频（静音）
      } else {
        video.pause();
        // 同步暂停右侧视频
      }
    }
  }, [externalIsPlaying]);

  // 防抖定时器引用
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncTimeRef = useRef<number>(0);
  const isSyncingRef = useRef<boolean>(false);
  const userInteractionRef = useRef<boolean>(false);

  // 监听外部时间变化，实现时间同步
  useEffect(() => {
    const video = videoRef.current;
    if (!video || externalCurrentTime === undefined) return;

    // 检查视频是否已加载元数据
    if (video.readyState < 1) {
      return;
    }

    // 检查时间是否在有效范围内
    if (externalCurrentTime < 0 || (video.duration && externalCurrentTime > video.duration)) {
      return;
    }

    // 如果正在同步中，跳过本次同步
    if (isSyncingRef.current) {
      return;
    }

    // 防抖机制：避免短时间内多次同步
    const now = Date.now();
    const debounceTime = 200; // 增加防抖时间，减少同步频率
    if (now - lastSyncTimeRef.current < debounceTime) {
      return;
    }

    // 检查时间差异
    const timeDiff = Math.abs(video.currentTime - externalCurrentTime);
    const syncThreshold = 1.0; // 提高同步阈值到1秒，减少不必要的同步
    
    // 只有在时间差异很大时才进行同步，避免在正常播放过程中的瞬跳
    if (timeDiff > syncThreshold) {
      // 清除之前的定时器
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // 延迟同步，给视频更多时间自然同步
      syncTimeoutRef.current = setTimeout(() => {
        if (video && !isSyncingRef.current) {
          // 再次检查时间差异，避免在延迟期间已经自然同步
          const currentTimeDiff = Math.abs(video.currentTime - externalCurrentTime);
          if (currentTimeDiff > syncThreshold) {
            isSyncingRef.current = true;
            // 同步时间
            try {
              video.currentTime = externalCurrentTime;
              lastSyncTimeRef.current = Date.now();
            } catch (error) {
              console.error('SubtitleDemo: 时间同步失败:', error);
            } finally {
              // 同步完成后重置标记
              setTimeout(() => {
                isSyncingRef.current = false;
              }, 100); // 增加重置延迟，避免频繁同步
            }
          }
        }
      }, 100); // 增加延迟到100ms，减少同步频率
    }

    // 清理函数
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [externalCurrentTime]);

  // 监听外部时间变化，实时更新字幕显示（已优化，避免闪烁）
  useEffect(() => {
    if (externalCurrentTime !== undefined) {
      // 直接调用渲染，renderSubtitles 内部已经做了节流处理
        renderSubtitles();
    }
  }, [externalCurrentTime, renderSubtitles]);

  // 监听视频事件
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      // 视频元数据加载完成
      resizeCanvas();
      
      // 通知父组件右侧视频已就绪
      if (onRightVideoReady) {
        onRightVideoReady();
      }
      
      // 元数据加载完成后，检查是否需要立即响应外部播放状态
      if (externalIsPlaying !== undefined && !userInteractionRef.current) {
        if (externalIsPlaying) {
          video.muted = true;
          video.play();
          // 元数据加载完成，立即同步播放右侧视频（静音）
        } else {
          video.pause();
          // 元数据加载完成，立即同步暂停右侧视频
        }
      }
    };

    const handleTimeUpdate = () => {
      // 视频自身的时间更新，直接渲染字幕（内部已做节流）
      renderSubtitles();
    };

    const handleError = (e: Event) => {
      console.error('视频加载失败:', e);
    };

    // 使用 ResizeObserver 实时监听 video 元素大小变化
    const setupResizeObserver = () => {
      if (!video) return;
      
      // 清理之前的观察者
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      
      // 创建新的 ResizeObserver，实时监听 video 尺寸变化
      resizeObserverRef.current = new ResizeObserver(() => {
        // 立即响应尺寸变化，无延迟
        resizeCanvas();
      });
      
      // 开始观察 video 元素
      resizeObserverRef.current.observe(video);
    };
    
    setupResizeObserver();

    const handlePlay = () => {
      // 只有在用户主动操作右侧视频时才触发外部同步
      // 避免在自动同步时触发循环
      if (onExternalPlay && !isSyncingRef.current && userInteractionRef.current) {
        // 用户操作右侧视频播放，同步左侧视频
        onExternalPlay();
      }
      // 重置用户交互标记
      userInteractionRef.current = false;
    };

    const handlePause = () => {
      // 只有在用户主动操作右侧视频时才触发外部同步
      // 避免在自动同步时触发循环
      if (onExternalPause && !isSyncingRef.current && userInteractionRef.current) {
        // 用户操作右侧视频暂停，同步左侧视频
        onExternalPause();
      }
      // 重置用户交互标记
      userInteractionRef.current = false;
    };

    const handleSeeked = () => {
      // 只有在用户主动操作右侧视频时才触发外部同步
      // 避免在自动同步时触发循环
      if (onExternalSeek && !video.seeking) {
        const timeDiff = Math.abs(video.currentTime - (externalCurrentTime || 0));
        if (timeDiff > 0.5) { // 只有差异较大时才同步
          // 用户操作右侧视频，同步左侧视频
          onExternalSeek(video.currentTime);
        }
      }
    };

    // 用户交互检测
    const handleUserInteraction = () => {
      userInteractionRef.current = true;
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('error', handleError);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    
    // 监听用户交互事件
    video.addEventListener('click', handleUserInteraction);
    video.addEventListener('touchstart', handleUserInteraction);
    video.addEventListener('keydown', handleUserInteraction);
    
    // 监听窗口大小变化（作为 ResizeObserver 的补充）
    const handleWindowResize = () => {
        resizeCanvas();
    };
    
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);
    
    // 页面可见性变化时重新计算
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 页面重新可见时，立即更新
        resizeCanvas();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('error', handleError);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('click', handleUserInteraction);
      video.removeEventListener('touchstart', handleUserInteraction);
      video.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // 清理 ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      
      // 取消未完成的动画帧请求
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      
      // 取消未完成的渲染请求
      if (renderRafIdRef.current !== null) {
        cancelAnimationFrame(renderRafIdRef.current);
        renderRafIdRef.current = null;
      }
    };
  }, [resizeCanvas, updateCurrentSubtitle]);

  return (
    <div className={`subtitle-demo-container ${className}`}>
      <div className="video-container">
        <video ref={videoRef}>
          <track ref={trackRef} kind="subtitles" srcLang="zh" label="中文字幕" />
          您的浏览器不支持视频播放。
        </video>
        <canvas 
          ref={canvasRef} 
          className="canvas-subtitle-overlay"
        />
      </div>
      
      {/* {showControls && (
        <div className="controls">
          <div className="video-controls">
            <h4>🎬 视频播放控制</h4>
            <button onClick={playVideo}>▶️ 播放</button>
            <button onClick={stopVideo}>⏹️ 停止</button>
          </div>
        </div>
      )} */}
    </div>
  );
});

SubtitleDemo.displayName = 'SubtitleDemo';

export default SubtitleDemo;
